// @flow
import { finectAPI } from '@finect/front-utils/finectAPI';
import { CONTENT_PIECE_DEFINITION } from './contentPieces/contentPieces';

export const getBankPageContents = (cookie: string, organization: Object, page: Object) => {
  const { tabs = [] } = page || {};

  const contentPieces = tabs.flatMap(({ blocks = [] }) => blocks).flatMap(({ pieces = [] }) => pieces);

  const promises = contentPieces.map((contentPiece: Object): any => {
    const contentAction = CONTENT_PIECE_DEFINITION[contentPiece.type] || CONTENT_PIECE_DEFINITION.NULL;

    const promise = contentAction(cookie, organization, contentPiece);

    return (
      promise
        .then((result: Object) => ({
          contentPiece,
          contentPieceData: result
        }))
        // Una pieza que falle no puede tumbar la ficha entera: se pinta sin ese módulo.
        // (getTablePieceContent y getNewsFeed no llevan onError, así que finectAPI relanza)
        .catch(() => ({
          contentPiece,
          contentPieceData: null
        }))
    );
  });

  return Promise.all(promises);
};

/**
 * Recibe un page y un array de pieces y devuelve un page con los pieces reemplazados por los datos
 */
const fillPageWithPiecesData = (page: Object, pieces: Array<Object>) => ({
  ...page,
  tabs: page.tabs.map(tab => ({
    ...tab,
    blocks: tab.blocks.map(block => ({
      ...block,
      pieces: block.pieces.map((piece) => {
        const asyncData = pieces.find(item => item.contentPiece.id === piece.id);

        return {
          ...piece,
          data: asyncData ? asyncData.contentPieceData : null
        };
      })
    }))
  }))
});

/**
 * Conserva el status HTTP del fallo para poder distinguir "este banco no existe" (404) de
 * "el API se ha caído" (5xx). Sin esto ambos casos son indistinguibles y la ficha acaba
 * respondiendo 200 en blanco. Ver getBankFetchState en scenes/Banks.
 */
const withHttpError = (params: Object) =>
  finectAPI({
    ...params,
    onError: (error: Object) => ({ httpErrorStatus: (error && error.status) || 502 })
  });

export function getOrganizationBankWithCMS(cookie: string, alias: string) {
  return Promise.all([
    // Fetch page
    withHttpError({
      version: 'v4',
      credentials: cookie,
      path: `contents/organizations/${alias}/page`,
      method: 'GET',
      onSuccess: ({ data }: { data: Object }) => data
    }),
    // Fetch organization
    withHttpError({
      version: 'v4',
      credentials: cookie,
      path: `contents/profiles/${alias}`,
      method: 'GET',
      onSuccess: ({ data }: { data: Object }) => data
    })
  ]).then((response) => {
    const failed = response.find(item => !item || item.httpErrorStatus);
    if (failed) return { error: (failed && failed.httpErrorStatus) || 502 };

    const [page, organization] = response;
    return Promise.all([getBankPageContents(cookie, organization, page)]).then(([pieces]) => ({
      page: fillPageWithPiecesData(page, pieces),
      organization
    }));
  });
}
